import context, { ILookupService, ServiceLookupModel, ServiceModel, ServiceStatus, ServiceType } from '@truckdown/systems';
import { defineComponent, ref, Ref, onMounted } from 'vue';
import { UserRightType, IUserCreator, AccountSummaryModel, MeasureUnit, UserStatus, AddUserAdminModel } from "@truckdown/accounts";
import validationError from '../../Validation/Error/Error.vue';
import { AddUserAdminValidator } from '../Validation/AddUserAdminValidator';
import { ChangePasswordValidator } from "../Validation/ChangePasswordValidator";
import { SearchRadiusPreference } from '@truckdown/locations';
import { formatRadius, showMessage } from '../../Utilities/Utilities';

interface IData {
    isLoading: Ref<boolean>,
    confirmPassword: Ref<string>,
    addUserModel: Ref<AddUserAdminModel>,
    allUserRights: Ref<any>,
    selectedRights: Ref<UserRightType[]>,
    errorMessage: Ref<string>,
    userValidator: Ref<any>,
    passwordValidator: Ref<any>,
    timeZones: Ref<string[]>,
    allServices: Ref<ServiceModel[]>,
    searchTermService: Ref<string>,
    passwordForm: Ref<IChangePasswordForm>
}

interface ISelectItem {
    value: string,
    display: string,
    terms?: string
};

interface IChangePasswordForm {
    newPassword: string,
    confirmPassword: string
}

const getData = function (props: any) {
    let model = {
        accountId: props.accountSummary.id,
        email: "",
        mobile: undefined,
        password: undefined,
        username: undefined,
        firstName: "",
        lastName: "",
        office: undefined,
        home: undefined,
        fax: undefined,
        timeZone: "Central Standard Time",
        measureUnit: MeasureUnit.Imperial,
        receiveProductUpdates: true,
        receiveReports: true,
        defaultServices: [],
        hideManagedServices: false,
        searchByDistance: false,
        searchPrivateRadius: SearchRadiusPreference.hundred,
        searchPreferredRadius: SearchRadiusPreference.fifteen,
        isPrimary: false,
        isBilling: false,
        suppressRatings: false,
        status: UserStatus.Active,
        allowRatings: true,
        editNotes: true,
        advancedPreferences: true,
        addFavoriteAvoid: true,
        isAdmin: false
    } as AddUserAdminModel;

    var passwordForm = {
        newPassword: "",
        confirmPassword: ""
    } as IChangePasswordForm;

    return {
        isLoading: ref<boolean>(false),
        confirmPassword: ref<string>(""),
        addUserModel: ref<AddUserAdminModel>(model),
        errorMessage: ref<string>(""),
        userValidator: ref<any>(new AddUserAdminValidator(model)),
        passwordValidator: ref<any>(new ChangePasswordValidator(passwordForm)),
        timeZones: ref<string[]>(props.timeZones),
        allServices: ref<ServiceModel[]>([]),
        searchTermService: ref<string>(''),
        passwordForm: ref<IChangePasswordForm>(passwordForm)
    } as IData;
}

const getMethods = function (data: IData, ctx: any) {
    return {
        closeAddUser: function () {
            ctx.emit('close-add');
        },
        addUserSubmit: async function () {
            if (!data.isLoading.value) {
                data.isLoading.value = true;

                try {
                    if (isAddUserModelValid(data)) {
                        data.addUserModel.value.password = data.passwordForm.value.newPassword.length > 0 ? data.passwordForm.value.newPassword : undefined;

                        let userManager = await context.getService<IUserCreator>('IUserCreator');
                        var response = await userManager.addUserAdmin(data.addUserModel.value);

                        if (!response || !response.ok) {
                            console.log("Error adding user: " + response.message);
                            showMessage("Error adding user: " + response.message, "danger", true, true);
                        } else {
                            ctx.emit("close-add", "User added successfully.");
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    data.isLoading.value = false;
                }
            }
        },
        selectTimeZone: function (timeZone: ISelectItem, ndx: number): void {
            data.addUserModel.value.timeZone = timeZone.value;
        },
        isSelectedRight: function (value: UserRightType) {
            return data.selectedRights.value.includes(value);
        },
        selectService: function (service: string) {
            if (service != "") {
                data.addUserModel.value.defaultServices.push(service);
            }
        },
        deselectService: function (service: string) {
            data.addUserModel.value.defaultServices = data.addUserModel.value.defaultServices.filter(s => s !== service);
        },
        serviceDisplay: function (serviceCode: string): string {
            var allServices = data.allServices.value as ServiceModel[];
            var service = allServices.filter(s => s.code == serviceCode);
            if (service.length == 1) {
                return service[0].name;
            } else {
                return serviceCode;
            }
        },
        measureUnitDisplay: function (measureUnit: MeasureUnit): string {
            return MeasureUnit[measureUnit];
        },
        searchDistanceDisplay: function (searchDistance: SearchRadiusPreference): string {
            return formatRadius(SearchRadiusPreference[searchDistance]);
        },
        getNotSelectedServices: function (): ServiceModel[] {
            var servicesToRemove = data.addUserModel.value.defaultServices;
            var allServices = data.allServices.value as ServiceModel[];

            var notSelectedList = allServices.filter(service => !servicesToRemove.includes(service["code"]));

            if (data.searchTermService.value != '') {
                return notSelectedList.filter(service => service.name.toLowerCase().indexOf(data.searchTermService.value.toLowerCase()) > -1);
            } else {
                return notSelectedList;
            }
        }
    }
}

const isAddUserModelValid = function (data: IData): boolean {
    if (data.passwordForm.value.newPassword.length > 0 ||
        data.passwordForm.value.confirmPassword.length > 0) {
        data.passwordValidator.value = new ChangePasswordValidator(data.passwordForm.value);
        return data.passwordValidator.value.validate();
    }

    data.userValidator.value = new AddUserAdminValidator(data.addUserModel.value);
    return data.userValidator.value.validate();
};

export default defineComponent({
    name: "AddUserAdmin",
    props: {
        accountSummary: Object as () => AccountSummaryModel,
        timeZones: {
            type: Array as () => string[],
            required: false
        },
        allServices: {
            type: Object as () => ServiceModel[],
            required: true
        }
    },
    emits: ['close-add'],
    components: {
        'error': validationError
    },
    setup: function (props, ctx: any) {
        const data = getData(props);
        const methods = getMethods(data, ctx);

        onMounted(async () => {
            var lookupService = await context.getService<ILookupService>('ILookupService');
            data.allServices.value = await lookupService.getServices({
                sites: [context.system],
                status: ServiceStatus.Active,
                types: [ServiceType.Service, ServiceType.DealerNetwork]
            } as ServiceLookupModel) 
        });

        return {
            ...data,
            ...methods,
            MeasureUnit,
            SearchRadiusPreference
        };
    }
});

